import { Target, CheckCircle2, Flag } from "lucide-react";
import { useAppState } from "@/context/StateContext";
import { fmt, pct } from "@/lib/format";

export default function MyGoal() {
  const { state } = useAppState();
  if (!state) return null;

  const { debts, totals, current_target } = state;
  const overall = pct(totals.total_paid_down, totals.total_original_debt);

  return (
    <div className="fade-up space-y-7" data-testid="my-goal-view">
      <header>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-[#DDE7DF] px-3 py-1 text-xs font-semibold uppercase tracking-[0.12em] text-[#164C3B]">
          <Flag className="h-3.5 w-3.5" /> My Goal
        </span>
        <h1 className="mt-3 font-serif text-4xl text-[#171717] sm:text-5xl">Become debt-free</h1>
        <p className="mt-2 text-lg text-[#525252]">
          One debt at a time, starting with the smallest.
        </p>
      </header>

      {/* Overall progress */}
      <div className="rounded-2xl border border-[#164C3B] bg-[#164C3B] p-7 text-white shadow-[0_8px_30px_-8px_rgba(22,76,59,0.5)]">
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">Remaining debt</p>
            <p className="mt-1.5 font-serif text-5xl tracking-tight">{fmt(totals.total_remaining_debt)}</p>
          </div>
          <p className="font-serif text-3xl text-[#C9A86A]">{overall}%</p>
        </div>
        <div className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-white/15">
          <div className="h-full rounded-full bg-[#C9A86A] transition-all duration-700" style={{ width: `${overall}%` }} />
        </div>
        <p className="mt-2.5 text-sm text-white/70">
          {current_target ? (
            <>Now working toward <span className="font-semibold text-white">{current_target.name}</span></>
          ) : (
            "You're debt-free 🎉"
          )}
        </p>
      </div>

      {/* Target order */}
      <div className="rounded-2xl pe-surface p-6">
        <div className="flex items-center gap-2">
          <Target className="h-5 w-5 text-[#2F6B57]" />
          <p className="font-serif text-xl text-[#164C3B]">What we pay off first</p>
        </div>
        <div className="mt-5 space-y-3">
          {debts.map((d) => {
            const isTarget = current_target && current_target.id === d.id;
            const p = pct(d.original_balance - d.balance, d.original_balance);
            return (
              <div
                key={d.id}
                className={`rounded-xl border p-4 ${isTarget ? "border-[#2F6B57] bg-[#F5F2EC]" : "border-[#E5E0D9] bg-white"}`}
                data-testid={`goal-debt-${d.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 font-medium text-[#171717]">
                    {d.name}
                    {isTarget && (
                      <span className="rounded-full bg-[#2F6B57] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                        Current target
                      </span>
                    )}
                    {d.eliminated && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-[#DDE7DF] px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-[#164C3B]">
                        <CheckCircle2 className="h-3 w-3" /> Paid off
                      </span>
                    )}
                  </span>
                  <span className="text-sm text-[#525252]">{fmt(d.balance)} left</span>
                </div>
                <div className="mt-2.5 h-2 w-full overflow-hidden rounded-full bg-[#F0EBE3]">
                  <div
                    className={`h-full rounded-full transition-all duration-700 ${d.eliminated ? "bg-[#2F6B57]" : "bg-[#164C3B]"}`}
                    style={{ width: `${p}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
